import { useCallback, useEffect, useRef, useState } from "react";

import {
  AthleteManagementError,
  AthleteProfile,
  AthleteServices,
} from "@/app/athlete/features/athlete-profile/services/athlete-services.ts";
import { useShowToast } from "@/components/toast-provider.tsx";
import { useAuth } from "@/features/Auth/stores/auth-store.ts";
import { kvBatch } from "@/lib/storage/kv-client.ts";

import {
  CoachedAthlete,
  CoachManagementError,
  CoachProfile,
  CoachServices,
  CoachUpdateParams,
} from "../services/coach-services.ts";

interface UseCoachProfileReturn {
  // Data
  profile: CoachProfile | null;
  athletes: CoachedAthlete[]; // Loaded separately from athlete profiles

  // Operations
  refreshProfile: () => Promise<CoachProfile | null>;
  refreshAthletes: () => Promise<void>; // New: reload athletes list
  updateProfile: (params: CoachUpdateParams) => Promise<CoachProfile | null>;
  initializeProfile: () => Promise<CoachProfile | null>;
  addAthlete: (athleteProfile: AthleteProfile) => Promise<void>;
  removeAthlete: (athleteId: string) => Promise<void>;
  updateAthlete: (
    athleteId: string,
    updates: { name?: string; access?: "full" | "read-only" },
  ) => Promise<void>;

  // Loading states
  isLoading: boolean;
  isRefreshing: boolean;
  isUpdating: boolean;
  isAddingAthlete: boolean;
  isRemovingAthlete: boolean;
  isUpdatingAthlete: boolean;
  isLoadingAthletes: boolean; // New: loading athletes

  // Error states
  error: string | null;
  refreshError: string | null;
  updateError: string | null;
  addAthleteError: string | null;
  removeAthleteError: string | null;
  updateAthleteError: string | null;

  // Utility
  clearErrors: () => void;
}

/**
 * Hook for managing coach profile
 * Automatically initializes/loads profile on mount
 */
export function useCoachProfile(): UseCoachProfileReturn {
  const { user } = useAuth();
  const { showError, showSuccess, showWarning } = useShowToast();

  // Refs to track initialization and toast functions
  const initializationAttemptedRef = useRef(false);
  const toastRef = useRef({ showError, showSuccess, showWarning });

  // Keep toast ref up to date
  useEffect(() => {
    toastRef.current = { showError, showSuccess, showWarning };
  }, [showError, showSuccess, showWarning]);

  // Data state
  const [profile, setProfile] = useState<CoachProfile | null>(null);
  const [athletes, setAthletes] = useState<CoachedAthlete[]>([]);

  // Loading states - start with true if we have a user but no profile
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isAddingAthlete, setIsAddingAthlete] = useState(false);
  const [isRemovingAthlete, setIsRemovingAthlete] = useState(false);
  const [isUpdatingAthlete, setIsUpdatingAthlete] = useState(false);
  const [isLoadingAthletes, setIsLoadingAthletes] = useState(false);

  // Error states
  const [error, setError] = useState<string | null>(null);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [addAthleteError, setAddAthleteError] = useState<string | null>(null);
  const [removeAthleteError, setRemoveAthleteError] = useState<string | null>(
    null,
  );
  const [updateAthleteError, setUpdateAthleteError] = useState<string | null>(
    null,
  );

  /**
   * Refresh profile from service
   */
  const refreshProfile = useCallback(async (): Promise<CoachProfile | null> => {
    if (!user?.uid) {
      const message = "User not authenticated";
      setRefreshError(message);
      return null;
    }

    setIsRefreshing(true);
    setRefreshError(null);

    try {
      const fetchedProfile = await CoachServices.getCoachProfile(user.uid);
      setProfile(fetchedProfile);
      return fetchedProfile;
    } catch (err) {
      const message =
        err instanceof CoachManagementError
          ? err.message
          : "Failed to refresh coach profile";
      setRefreshError(message);
      showError(message);
      return null;
    } finally {
      setIsRefreshing(false);
    }
  }, [user?.uid, showError]);

  /**
   * Update coach profile
   */
  const updateProfile = useCallback(
    async (params: CoachUpdateParams): Promise<CoachProfile | null> => {
      if (!user?.uid) {
        const message = "User not authenticated";
        setUpdateError(message);
        showError(message);
        return null;
      }

      setIsUpdating(true);
      setUpdateError(null);

      try {
        const updatedProfile = await CoachServices.updateCoachProfile(
          user.uid,
          params,
        );
        setProfile(updatedProfile);
        showSuccess("Profile updated successfully");
        return updatedProfile;
      } catch (err) {
        const message =
          err instanceof CoachManagementError
            ? err.message
            : "Failed to update coach profile";
        setUpdateError(message);
        showError(message);
        return null;
      } finally {
        setIsUpdating(false);
      }
    },
    [user?.uid, showSuccess, showError],
  );

  /**
   * Load athletes for the current coach from athlete profiles
   */
  const refreshAthletes = useCallback(async (): Promise<void> => {
    if (!user?.uid) {
      console.warn("Cannot load athletes: user not authenticated");
      return;
    }

    setIsLoadingAthletes(true);
    try {
      const loadedAthletes = await AthleteServices.getAthletesForCoach(
        user.uid,
      );
      setAthletes(loadedAthletes);
    } catch (error) {
      console.error("Failed to load athletes:", error);
      // Don't show error toast - graceful degradation
      setAthletes([]);
    } finally {
      setIsLoadingAthletes(false);
    }
  }, [user?.uid]);

  /**
   * Initialize profile - creates if doesn't exist, loads if it does
   */
  const initializeProfile =
    useCallback(async (): Promise<CoachProfile | null> => {
      console.log("[useCoachProfile] initializeProfile called", {
        uid: user?.uid,
        email: user?.email,
        name: user?.name,
      });

      if (!user?.uid || !user.email) {
        const message = "User authentication incomplete";
        console.error("[useCoachProfile] User auth incomplete", {
          hasUid: !!user?.uid,
          hasEmail: !!user?.email,
        });
        setError(message);
        return null;
      }

      setIsLoading(true);
      setError(null);

      try {
        // Try to get existing profile
        let fetchedProfile: CoachProfile;
        try {
          console.log(
            "[useCoachProfile] Fetching existing profile for",
            user.uid,
          );
          fetchedProfile = await CoachServices.getCoachProfile(user.uid);
          console.log("[useCoachProfile] Profile fetched successfully");
        } catch (err) {
          // If profile doesn't exist, create a new one
          if (err instanceof CoachManagementError && err.code === "NOT_FOUND") {
            console.log("[useCoachProfile] Profile not found, creating new one");
            fetchedProfile = await CoachServices.createCoachProfile(
              user.uid,
              user.email,
              user.name || "Coach",
            );
            console.log("[useCoachProfile] Profile created successfully");
            toastRef.current.showSuccess("Coach profile created successfully");
          } else {
            throw err;
          }
        }

        console.log("[useCoachProfile] Setting profile in state", {
          profileUid: fetchedProfile.uid,
        });
        setProfile(fetchedProfile);
        return fetchedProfile;
      } catch (err) {
        const message =
          err instanceof CoachManagementError
            ? err.message
            : "Failed to initialize coach profile";
        console.error("[useCoachProfile] Error initializing profile", err);
        setError(message);
        toastRef.current.showError(message);
        return null;
      } finally {
        setIsLoading(false);
      }
    }, [user?.uid, user?.email, user?.name]);

  /**
   * Add an athlete to coach's profile
   * Now only updates the athlete's coaches array, then reloads athletes
   */
  const addAthlete = useCallback(
    async (athleteProfile: AthleteProfile): Promise<void> => {
      if (!user?.uid || !profile) {
        const message = "User not authenticated or profile not loaded";
        setAddAthleteError(message);
        showError(message);
        return;
      }

      setIsAddingAthlete(true);
      setAddAthleteError(null);

      try {
        // Check if athlete is already in coach's list
        const athleteExists = athletes.some(
          (a) => a.athleteId === athleteProfile.uid,
        );

        if (athleteExists) {
          const message = "Athlete is already assigned to you";
          setAddAthleteError(message);
          showError(message);
          setIsAddingAthlete(false);
          return;
        }

        // Check if coach already exists in athlete's coaches array
        const coachExists = athleteProfile.coaches.some(
          (c) => c.coachId === user.uid,
        );

        if (coachExists) {
          const message = "Athlete is already assigned to you";
          setAddAthleteError(message);
          showError(message);
          setIsAddingAthlete(false);
          return;
        }

        // Update athlete profile to add this coach
        await AthleteServices.updateAthleteCoaches(
          athleteProfile.uid,
          {
            coachId: user.uid,
            name: profile.name,
            primary: athleteProfile.coaches.length === 0, // First coach is primary
          },
          user.uid,
        );

        // Reload athletes list
        await refreshAthletes();

        showSuccess(`${athleteProfile.name} added successfully`);
      } catch (err) {
        const message =
          err instanceof AthleteManagementError
            ? err.message
            : "Failed to add athlete";
        setAddAthleteError(message);
        showError(message);
      } finally {
        setIsAddingAthlete(false);
      }
    },
    [user?.uid, profile, athletes, showSuccess, showError, refreshAthletes],
  );

  /**
   * Remove an athlete from coach's profile
   * Now only updates the athlete's coaches array, then reloads athletes
   */
  const removeAthlete = useCallback(
    async (athleteId: string): Promise<void> => {
      if (!user?.uid || !profile) {
        const message = "User not authenticated or profile not loaded";
        setRemoveAthleteError(message);
        showError(message);
        return;
      }

      setIsRemovingAthlete(true);
      setRemoveAthleteError(null);

      try {
        const athlete = athletes.find((a) => a.athleteId === athleteId);
        if (!athlete) {
          const message = "Athlete not found in coach profile";
          setRemoveAthleteError(message);
          showError(message);
          setIsRemovingAthlete(false);
          return;
        }

        let athleteProfile;
        try {
          athleteProfile = await AthleteServices.getAthleteProfile(athleteId);
        } catch (err) {
          if (
            err instanceof AthleteManagementError &&
            err.code === "NOT_FOUND"
          ) {
            showWarning(`Athlete profile not found during removal`);
            setIsRemovingAthlete(false);
            return;
          } else {
            throw err;
          }
        }

        // Remove coach from athlete's coaches array
        const updatedAthleteProfile: AthleteProfile = {
          ...athleteProfile,
          coaches: athleteProfile.coaches.filter((c) => c.coachId !== user.uid),
        };

        await kvBatch([
          {
            type: "set" as const,
            key: `athlete:${athleteId}`,
            value: updatedAthleteProfile,
            syncMetadata: {
              type: "ATHLETE_PROFILE",
              entityId: "Remove coach from athlete",
              userId: athleteId,
              createdBy: user.uid,
            },
          },
        ]);

        // Emit sync event to notify other listeners
        const { syncEventEmitter } = await import("@/lib/storage/sync-event-emitter.ts");
        syncEventEmitter.emit(`athlete:${athleteId}`, true);

        // Reload athletes list
        await refreshAthletes();

        showSuccess(`${athlete.name} removed successfully`);
      } catch (err) {
        const message =
          err instanceof AthleteManagementError
            ? err.message
            : "Failed to remove athlete";
        setRemoveAthleteError(message);
        showError(message);
      } finally {
        setIsRemovingAthlete(false);
      }
    },
    [
      user?.uid,
      profile,
      athletes,
      showSuccess,
      showError,
      showWarning,
      refreshAthletes,
    ],
  );

  /**
   * Update an athlete's details (name and access level)
   * Updates the athlete profile and/or the coach-athlete relationship
   */
  const updateAthlete = useCallback(
    async (
      athleteId: string,
      updates: { name?: string; access?: "full" | "read-only" },
    ): Promise<void> => {
      if (!user?.uid || !profile) {
        const message = "User not authenticated or profile not loaded";
        setUpdateAthleteError(message);
        showError(message);
        return;
      }

      setIsUpdatingAthlete(true);
      setUpdateAthleteError(null);

      try {
        const athlete = athletes.find((a) => a.athleteId === athleteId);
        if (!athlete) {
          const message = "Athlete not found";
          setUpdateAthleteError(message);
          showError(message);
          setIsUpdatingAthlete(false);
          return;
        }

        let athleteProfile;
        try {
          athleteProfile = await AthleteServices.getAthleteProfile(athleteId);
        } catch (err) {
          throw new AthleteManagementError(
            "Athlete profile not found",
            "NOT_FOUND",
          );
        }

        // Update athlete profile with new data
        const updatedProfile: AthleteProfile = {
          ...athleteProfile,
          name: updates.name ?? athleteProfile.name,
          coaches: athleteProfile.coaches.map((c) =>
            c.coachId === user.uid
              ? { ...c, access: updates.access ?? c.access }
              : c,
          ),
        };

        await kvBatch([
          {
            type: "set" as const,
            key: `athlete:${athleteId}`,
            value: updatedProfile,
            syncMetadata: {
              type: "ATHLETE_PROFILE",
              entityId: "Update athlete details",
              userId: athleteId,
              createdBy: user.uid,
            },
          },
        ]);

        // Emit sync event to notify other listeners
        const { syncEventEmitter } = await import("@/lib/storage/sync-event-emitter.ts");
        syncEventEmitter.emit(`athlete:${athleteId}`, true);

        // Reload athletes list
        await refreshAthletes();

        showSuccess("Athlete updated successfully");
      } catch (err) {
        const message =
          err instanceof AthleteManagementError
            ? err.message
            : "Failed to update athlete";
        setUpdateAthleteError(message);
        showError(message);
      } finally {
        setIsUpdatingAthlete(false);
      }
    },
    [user?.uid, profile, athletes, showSuccess, showError, refreshAthletes],
  );

  /**
   * Clear all error states
   */
  const clearErrors = useCallback(() => {
    setError(null);
    setRefreshError(null);
    setUpdateError(null);
    setAddAthleteError(null);
    setRemoveAthleteError(null);
    setUpdateAthleteError(null);
  }, []);

  /**
   * Auto-initialize profile on mount
   */
  useEffect(() => {
    console.log("[useCoachProfile] Effect running", {
      hasUser: !!user,
      uid: user?.uid,
      email: user?.email,
      name: user?.name,
      hasProfile: !!profile,
      isLoading,
      initializationAttempted: initializationAttemptedRef.current,
    });

    if (!user?.uid) {
      console.log("[useCoachProfile] No user UID, skipping initialization");
      // Reset initialization flag when user logs out
      initializationAttemptedRef.current = false;
      return;
    }

    // Only initialize if we don't have a profile yet and haven't attempted initialization
    if (!profile && !isLoading && !initializationAttemptedRef.current) {
      console.log("[useCoachProfile] Calling initializeProfile");
      initializationAttemptedRef.current = true;
      void initializeProfile();
    } else {
      console.log(
        "[useCoachProfile] Skipping initialization",
        {
          hasProfile: !!profile,
          isLoading,
          alreadyAttempted: initializationAttemptedRef.current,
        },
      );
    }
  }, [user?.uid, profile, isLoading, initializeProfile]);

  /**
   * Auto-load athletes when profile is available
   */
  useEffect(() => {
    if (profile?.uid) {
      void refreshAthletes();
    }
  }, [profile?.uid, refreshAthletes]);

  /**
   * Listen for athlete deletion/update events and auto-refresh
   */
  useEffect(() => {
    if (!profile?.uid) return;

    // Import sync event emitter dynamically
    const setupListener = async () => {
      const { syncEventEmitter } = await import("@/lib/storage/sync-event-emitter.ts");

      // Listen for any athlete changes (athlete:*)
      const unsubscribe = syncEventEmitter.on("athlete:*", (key, success) => {
        if (success) {
          console.log("[useCoachProfile] Athlete sync event received, refreshing athletes:", key);
          void refreshAthletes();
        }
      });

      return unsubscribe;
    };

    let unsubscribe: (() => void) | undefined;
    void setupListener().then((unsub) => {
      unsubscribe = unsub;
    });

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [profile?.uid, refreshAthletes]);

  return {
    // Data
    profile,
    athletes,

    // Operations
    refreshProfile,
    refreshAthletes,
    updateProfile,
    initializeProfile,
    addAthlete,
    removeAthlete,
    updateAthlete,

    // Loading states
    isLoading,
    isRefreshing,
    isUpdating,
    isAddingAthlete,
    isRemovingAthlete,
    isUpdatingAthlete,
    isLoadingAthletes,

    // Error states
    error,
    refreshError,
    updateError,
    addAthleteError,
    removeAthleteError,
    updateAthleteError,

    // Utility
    clearErrors,
  };
}
export default useCoachProfile;
